import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { combineLatest } from 'rxjs';
import { InputFieldComponent } from '../../../../components/ui/inputField/inputField.component';
import { ImageInputComponent } from '../../../../components/inputs/image-input/image-input.component';
import { CustomizableButtonComponent } from '../../../../components/ui/customizable-button/customizable-button.component';
import { StatusComponent } from '../../../../components/ui/status/status.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { BroadcastMessageService } from '../../../../services/broadcast/broadcast.service';
import { ISenderID } from '../../../../services/broadcast/types/broadcastTypes';
import { ToastService } from '../../../../services/toast/toast.service';
import { TBlkCxpRole } from '../../../../types/settingsDataModule';
import { countriesOperator, getCountryCode } from '../../../../../utils/utils';
import { countries } from '../../../../../utils/countriesList';
import { industries } from '../../../../../utils/staticData';
import { formatWhen } from '../../../../../utils/sms';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

/** One sender ID as the messaging panel shows it. */
interface SenderRow {
  key: string;
  name: string;
  kind: string;
  statusText: string;
  status: string;
  added: string;
}

/** An identifier support may ask for, with a copy button. */
interface OrgFact {
  label: string;
  value: string;
}

@Component({
  selector: 'app-organization-settings',
  standalone: true,
  imports: [
    CommonModule,
    InputFieldComponent,
    ReactiveFormsModule,
    ImageInputComponent,
    CustomizableButtonComponent,
    NgxMatInputTelComponent,
    StatusComponent,
    MatTooltipModule,
  ],
  templateUrl: './organization-settings.component.html',
  styleUrls: ['../settings-ui.css', './organization-settings.component.css'],
})
export class OrganizationSettingsComponent implements OnInit, OnDestroy {
  private builder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private permissions = inject(PermissionsService);
  private broadcastService = inject(BroadcastMessageService);
  private toast = inject(ToastService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  settingsService = inject(SettingsService);
  submitted: boolean = false;
  isUploadingLogo: boolean = false;
  isUpdatingOrganization: boolean = false;
  isDeletingLogo: boolean = false;

  // Feedback: confirmations go to a toast; a failed save stays above the save bar.
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;
  saveError = '';

  // variables
  countries = countries;
  industries = industries.map((industry) => ({
    value: industry.value,
    label: this.sentenceCase(industry.label),
  }));

  // reactive data
  institutionLogo: string = '';
  /** The organization as IAM returns it. */
  organization: any = null;
  /** False until the institution has loaded once. */
  loaded = false;

  /** Administrators and account managers can change these details. */
  canEdit = true;

  // Messaging identity
  senderRows: SenderRow[] = [];
  senderIdsLoading = true;
  senderIdsFailed = false;

  institutionForm = this.builder.group({
    name: ['', Validators.required],
    size: [''],
    primaryContactNumber: [''],
    emailAddress: ['', [Validators.email]],
    website: [''],
    address: [''],
    city: [''],
    country: [''],
    countryCode: [''],
    industryType: [''],
    registrationNumber: [''],
    description: [''],
  });

  /** The last values the server sent, so Discard can put them back. */
  private savedValues: Record<string, any> | null = null;

  get orgName(): string {
    return this.organization?.name || this.institutionForm.getRawValue().name || '';
  }

  get countryCode(): string {
    return this.institutionForm.getRawValue().countryCode || '';
  }

  get hasChanges(): boolean {
    return this.institutionForm.dirty;
  }

  /** Identifiers support asks for, when the organization carries them. */
  get facts(): OrgFact[] {
    const org = this.organization;
    if (!org) return [];
    const attributes = org.attributes || {};
    const list: OrgFact[] = [
      { label: 'Alias', value: org.alias || '' },
      { label: 'Organization ID', value: attributes.organisationId || attributes.organizationId || org.id || '' },
      { label: 'SMSC tenant', value: attributes.blackkonnectSmscTenantId || '' },
    ];
    return list.filter((fact) => !!fact.value);
  }

  /**
   * The picker's own name for an ISO code, or '' when its list has none.
   * ISO official names ("United States of America") are not all in the list,
   * and a name the select cannot show must not be saved back.
   */
  private countryNameFor(code: string | null | undefined): string {
    const iso = (code || '').trim().toUpperCase();
    if (!iso) return '';
    const official = countriesOperator.getName(iso, 'en');
    if (official && countries.includes(official)) return official;
    return countries.find((name) => getCountryCode(name)?.countryCode === iso) || '';
  }

  updateCountryCode() {
    const code = getCountryCode(this.institutionForm.value.country as string)?.countryCode;
    code && this.institutionForm.get('countryCode')?.setValue(code as string);
  }

  get emailErrorMsg() {
    const email = this.institutionForm.get('emailAddress');
    if (email?.errors?.['email']) {
      return 'Enter a full email address, like support@company.com.';
    } else if (email?.errors?.['required']) {
      return 'Enter an email address.';
    } else {
      return '';
    }
  }

  ngOnInit() {
    this.institutionForm.get('countryCode')?.disable();

    // Only people who may edit get an editable form.
    combineLatest([
      this.settingsService.getUserRoles,
      this.permissions.hasPermission('ACCOUNT_MANAGEMENT', 'edit-account-settings'),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([roles, allowed]: [TBlkCxpRole[], boolean]) => {
        // Until the roles load, assume yes rather than flash a locked form.
        this.setEditable((roles || []).length === 0 ? true : allowed);
      });

    // Surface organization fetch failures
    this.settingsService.onErrorMessage
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ message, source }) => {
        if (source === 'fetchUserInstitution') {
          this.displayFeedback(`Your organization’s details did not load. ${message}`, 'error');
        }
      });

    this.settingsService.getOrganizationLogoData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((logoData) => {
        if (this.institutionLogo && this.institutionLogo.startsWith('blob:') && this.institutionLogo !== logoData) {
          URL.revokeObjectURL(this.institutionLogo);
        }

        if (logoData) {
          this.institutionLogo = logoData;
        } else if (logoData === null) {
          this.institutionLogo = '';
        }

        this.cdr.markForCheck();
      });

    this.settingsService.fetchUserInstitution();

    let logoRequested = false;
    this.settingsService.getUserInstitutionData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (!data) return;
        this.organization = data;
        this.loaded = true;

        const attributes = data.attributes || {};
        this.savedValues = {
          name: data.name,
          size: data.size,
          // Older records keep contact details in attributes; newer ones at the top level.
          primaryContactNumber: attributes.primaryContactNumber || data.primaryContactNumber || '',
          emailAddress: attributes.email || data.emailAddress || data.email || '',
          website: data.website || '',
          address: attributes.address || data.address || '',
          city: attributes.city || data.city || '',
          country: data.country || this.countryNameFor(data.countryCode),
          countryCode: data.countryCode || '',
          industryType: attributes.industryType || data.industryType || '',
          registrationNumber: data.registrationNumber || '',
          description: data.description || '',
        };

        // A background refresh never overwrites unsaved edits.
        if (!this.institutionForm.dirty || this.isUpdatingOrganization) {
          this.institutionForm.patchValue(this.savedValues);
          this.institutionForm.markAsPristine();
        }

        // The logo is fetched once per visit, and again after an upload.
        if (!logoRequested) {
          logoRequested = true;
          this.fetchOrganizationLogo();
        }

        this.cdr.markForCheck();
      });

    this.loadSenderIds();
  }

  private setEditable(allowed: boolean) {
    this.canEdit = allowed;
    if (allowed) {
      if (this.institutionForm.disabled) {
        this.institutionForm.enable({ emitEvent: false });
      }
      this.institutionForm.get('countryCode')?.disable({ emitEvent: false });
    } else if (this.institutionForm.enabled) {
      this.institutionForm.disable({ emitEvent: false });
    }
  }

  /** Sender IDs come from the same endpoint the composer uses. */
  private loadSenderIds() {
    this.senderIdsLoading = true;
    this.senderIdsFailed = false;
    this.broadcastService
      .fetchSenderIDs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list: ISenderID[]) => {
          this.senderRows = (Array.isArray(list) ? list : []).map((item, index) =>
            this.toSenderRow(item, index)
          );
          this.senderIdsLoading = false;
        },
        error: () => {
          this.senderIdsLoading = false;
          this.senderIdsFailed = true;
        },
      });
  }

  retrySenderIds() {
    this.loadSenderIds();
  }

  private toSenderRow(item: ISenderID, index: number): SenderRow {
    const name = (item?.senderId || '').toString();
    const raw: unknown = item?.status;
    let statusText = 'Unknown';
    // app-status reads its colour from this word: "inactive" draws a grey dot.
    let status = 'inactive';
    // Negative words first: "inactive" and "deactivated" also contain "activ".
    if (raw === 0 || (typeof raw === 'string' && /inactive|deactiv|disabl|reject/i.test(raw))) {
      statusText = 'Inactive';
      status = 'disabled';
    } else if (raw === 1 || (typeof raw === 'string' && /approv|active/i.test(raw))) {
      statusText = 'Active';
      status = 'success';
    } else if (typeof raw === 'string' && /pend|review/i.test(raw)) {
      statusText = 'Pending';
      status = 'pending';
    }
    return {
      key: `${item?.id ?? index}-${name}`,
      name: name || '—',
      // Digits only: a short code or long number. Anything else: a name.
      kind: /^\+?\d+$/.test(name) ? 'Number' : 'Name',
      statusText,
      status,
      added: item?.dateCreated ? formatWhen(item.dateCreated) : '—',
    };
  }

  copy(fact: OrgFact) {
    if (!this.isBrowser || !navigator?.clipboard) {
      this.toast.error('Copying is not available in this browser. Select the text and copy it instead.');
      return;
    }
    navigator.clipboard.writeText(fact.value).then(
      () => this.toast.success(`${fact.label} copied.`),
      () => this.toast.error('Copying failed. Select the text and copy it instead.')
    );
  }

  fetchOrganizationLogo() {
    this.settingsService.getOrganizationLogo().subscribe({
      error: () => {
        // No logo, or it failed to load: the placeholder tile stays.
      },
    });
  }

  addLogo(imageFile: File | null) {
    if (!imageFile) {
      return;
    }

    this.isUploadingLogo = true;

    this.settingsService.addOrganizationLogo(imageFile).subscribe({
      next: () => {
        setTimeout(() => {
          this.fetchOrganizationLogo();
          this.settingsService.fetchUserInstitution();
          this.isUploadingLogo = false;
          this.displayFeedback('Logo updated.', 'success');
        }, 500);
      },
      error: (error) => {
        const errorMessage =
          error?.error?.error || error?.error?.message || 'The logo did not upload. Try a smaller PNG or JPG.';
        this.displayFeedback(errorMessage, 'error');
        this.isUploadingLogo = false;
      },
    });
  }

  deleteLogo() {
    this.isDeletingLogo = true;

    this.settingsService.removeOrganizationLogo().subscribe({
      next: () => {
        this.isDeletingLogo = false;
        this.displayFeedback('Logo removed.', 'success');
      },
      error: (error) => {
        this.isDeletingLogo = false;
        const errorMessage =
          error?.status === 500
            ? 'The logo was not removed because of a problem on our side. Try again in a moment.'
            : error?.error?.detail ||
              error?.error?.message ||
              error?.message ||
              'The logo was not removed. Try again.';
        this.displayFeedback(errorMessage, 'error');
      },
    });
  }

  /** Puts back what is saved and drops the unsaved edits. */
  discardChanges() {
    this.submitted = false;
    this.saveError = '';
    if (this.savedValues) {
      this.institutionForm.patchValue(this.savedValues);
    }
    this.institutionForm.markAsPristine();
  }

  handleSubmit() {
    if (!this.canEdit) return;
    this.submitted = true;
    this.saveError = '';

    if (this.institutionForm.invalid) {
      this.saveError = 'Some details need fixing. Check the fields marked in red.';
      return;
    }

    // use the institution ID instead
    const organizationId = this.settingsService.institutionId;
    if (!organizationId) {
      this.saveError = 'We could not tell which organization to update. Reload the page and try again.';
      return;
    }

    // Get all form values including disabled fields (like countryCode)
    const formValues = this.institutionForm.getRawValue();

    // Prepare the data for update (only fields that can be updated)
    const updateData = {
      primaryContactNumber: formValues.primaryContactNumber!,
      emailAddress: formValues.emailAddress!,
      website: formValues.website || '',
      country: formValues.country || countriesOperator.getName(formValues.countryCode as string, 'eng'),
      countryCode: formValues.countryCode || '',
      address: formValues.address || '',
      city: formValues.city || '',
      industryType: formValues.industryType!,
      registrationNumber: formValues.registrationNumber || '',
      description: formValues.description!,
    };

    this.isUpdatingOrganization = true;

    this.settingsService.updateOrganizationDetails(organizationId, updateData).subscribe({
      next: () => {
        this.isUpdatingOrganization = false;
        this.submitted = false;
        this.institutionForm.markAsPristine();
        this.displayFeedback('Organization details saved.', 'success');
      },
      error: (error) => {
        this.isUpdatingOrganization = false;
        this.saveError =
          error?.error?.message ||
          error?.message ||
          'The details were not saved. Check your connection and try again.';
      },
    });
  }

  displayFeedback(message: string, type: FeedbackType) {
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    if (type === 'error' || type === 'warning') {
      this.toast.error(message);
    } else {
      this.toast.success(message);
    }
  }

  private sentenceCase(text: string): string {
    const words = (text || '').replace(/[_-]+/g, ' ').trim().toLowerCase();
    return words ? words[0].toUpperCase() + words.slice(1) : text;
  }

  ngOnDestroy() {
    // Subscriptions end with the component (takeUntilDestroyed).
  }
}
